import React, { useEffect, useState, useCallback } from "react";
import firebaseApp from "./fireconnect";
import ProductCard from "./ProductCard";
import { Offcanvas, Form, Button } from "react-bootstrap";
import ReactSlider from "react-slider";
import { useDataContext } from "./DataContext";

const HomePageProducts = () => {
  const {
    products: cachedProducts,
    setProducts: setCachedProducts,
    productsFetched,
    setProductsFetched,
    productsLastDoc: cachedLastDoc,
    setProductsLastDoc: setCachedLastDoc,
    productsHasMore: cachedHasMore,
    setProductsHasMore: setCachedHasMore,
  } = useDataContext();

  const [datamng, setmngData] = useState(cachedProducts);
  const [filteredData, setFilteredData] = useState(cachedProducts);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [minPrice, setMinPrice] = useState(0);
  const [maxPrice, setMaxPrice] = useState(35000);
  const [loading, setLoading] = useState(!productsFetched);
  const [loadingMore, setLoadingMore] = useState(false);
  const [showSidebar, setShowSidebar] = useState(false);
  const [value, setValue] = useState([0, 35000]);
  const [lastDoc, setLastDoc] = useState(cachedLastDoc);
  const [hasMore, setHasMore] = useState(cachedHasMore);

  const db = firebaseApp.firestore();

  useEffect(() => {
    setValue([minPrice, maxPrice]);
  }, [minPrice, maxPrice]);

  const fetchInitialProducts = useCallback(async () => {
    // If data is already cached, use it
    if (productsFetched && cachedProducts.length > 0) {
      setmngData(cachedProducts);
      setFilteredData(cachedProducts);
      setLastDoc(cachedLastDoc);
      setHasMore(cachedHasMore);
      setLoading(false);
      return;
    }

    setLoading(true);
    const query = db.collection("Product").orderBy("discountedPrice").limit(8);

    const snapshot = await query.get();
    const products = snapshot.docs.map((doc) => {
      const data = doc.data();
      return {
        ...data,
        image: [data.image?.[0]],
        id: doc.id,
      };
    });

    setmngData(products);
    setFilteredData(products);
    const newLastDoc = snapshot.docs[snapshot.docs.length - 1] || null;
    setLastDoc(newLastDoc);
    const newHasMore = !snapshot.empty;
    setHasMore(newHasMore);
    setLoading(false);

    // Update cache
    setCachedProducts(products);
    setProductsFetched(true);
    setCachedLastDoc(newLastDoc);
    setCachedHasMore(newHasMore);
  }, [db, productsFetched, cachedProducts, cachedLastDoc, cachedHasMore, setCachedProducts, setProductsFetched, setCachedLastDoc, setCachedHasMore]);

  const fetchMoreProducts = useCallback(async () => {
    if (!lastDoc || !hasMore) return;

    setLoadingMore(true);
    const query = db
      .collection("Product")
      .orderBy("discountedPrice")
      .startAfter(lastDoc)
      .limit(8);

    const snapshot = await query.get();
    const newProducts = snapshot.docs.map((doc) => {
      const data = doc.data();
      return {
        ...data,
        image: [data.image?.[0]],
        id: doc.id,
      };
    });

    setmngData((prev) => [...prev, ...newProducts]);
    setFilteredData((prev) => [...prev, ...newProducts]);
    setLastDoc(snapshot.docs[snapshot.docs.length - 1] || null);
    setHasMore(!snapshot.empty);
    setLoadingMore(false);
  }, [db, lastDoc, hasMore]);

  useEffect(() => {
    fetchInitialProducts();
  }, [fetchInitialProducts]);

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY || document.documentElement.scrollTop;
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;

      if (
        scrollTop + windowHeight >= documentHeight - 1450 &&
        !loadingMore &&
        hasMore
      ) {
        fetchMoreProducts();
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [fetchMoreProducts, loadingMore, hasMore]);

  const filterProducts = useCallback(() => {
    let filtered = datamng;

    if (selectedCategories.length > 0) {
      filtered = filtered.filter((product) =>
        selectedCategories.includes(product.category)
      );
    }

    filtered = filtered.filter((product) => {
      if (maxPrice < 35000) {
        return (
          product.discountedPrice >= minPrice &&
          product.discountedPrice <= maxPrice
        );
      } else {
        return product.discountedPrice >= minPrice;
      }
    });

    setFilteredData(filtered);
  }, [datamng, selectedCategories, minPrice, maxPrice]);

  useEffect(() => {
    filterProducts();
  }, [filterProducts]);

  const handleChange = (newValue) => {
    setValue(newValue);
    handlePriceChange(newValue);
  };

  const handleCategoryChange = (e) => {
    const { value, checked } = e.target;
    setSelectedCategories((prev) =>
      checked ? [...prev, value] : prev.filter((cat) => cat !== value)
    );
  };

  const handlePriceChange = (newRange) => {
    setMinPrice(newRange[0]);
    setMaxPrice(newRange[1]);
  };

  const colClass = "col-6 col-sm-6 col-md-4 col-lg-3";

  return (
    <section>
      <div className="text-center section-title pt-lg-5 pt-4 pb-lg-4 pb-3">
        <h2 className="fw-bold text-dark mb-2">Our Latest Products</h2>
        <p className="text-muted mb-0">Discover premium water filtration solutions for your home</p>
      </div>

      <section className="pt-4 pb-5 p-0">
        <div className="container">
          <div className="row">
            <div className="d-none d-lg-block filterProducts mb-4 p-0 pe-4">
              <h5 className="filter-title fw-bold mb-4">Filter by Price</h5>
              {/* Sidebar Content - kept same but visible */}
              <div className="price-range-container">
                <h6 className="price-range-title fw-bold mb-4">
                  Price Range
                </h6>
                <ReactSlider
                  min={0}
                  max={35000}
                  value={value}
                  onChange={handleChange}
                  renderTrack={(props, state) => {
                    const { key, style, ...restProps } = props;
                    return (
                      <div
                        key={key}
                        {...restProps}
                        className="slider-track"
                        style={{
                          ...style,
                          background:
                            state.index === 1 ? "#032a54" : "#ccc",
                        }}
                      />
                    );
                  }}
                  renderThumb={(props) => {
                    const { key, style, ...restProps } = props;
                    return (
                      <div
                        key={key}
                        {...restProps}
                        className="slider-thumb"
                        style={{
                          ...style,
                          border: "1px solid #032a54",
                        }}
                      />
                    );
                  }}
                />
                <div className="price-range-display pt-4">
                  <span>₹{value[0]}</span>{" "}
                  <span>
                    {value[1] >= 35000 ? "₹35000+" : `₹${value[1]}`}
                  </span>
                </div>
              </div>

              <h5 className="filter-title fw-bold mb-3 mt-4">
                Filter by Category
              </h5>
              {["Filter", "Geysers", "Pump", "Memorn"].map((category) => (
                <div key={category} className="form-check">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    id={category}
                    value={category}
                    onChange={handleCategoryChange}
                    checked={selectedCategories.includes(category)}
                  />
                  <label className="form-check-label" htmlFor={category}>
                    {category}
                  </label>
                </div>
              ))}
            </div>

            <div className="productsPart p-md-0 p-sm-0">
              <div className="d-lg-none p-0 d-flex justify-content-end">
                <Button
                  className="d-lg-none mb-4 px-3 py-1 SearchFilters"
                  variant="outline-dark"
                  onClick={() => setShowSidebar(true)}
                >
                  <i className="bi bi-funnel-fill me-2 fs-5"></i>
                  <span>Search Filters</span>
                </Button>
              </div>

              <div className="row px-md-0 px-1">
                {loading ? (
                  Array.from({ length: 8 }).map((_, index) => (
                    <ProductCard
                      key={index}
                      loading={true}
                      colClass={colClass}
                    />
                  ))
                ) : filteredData.length === 0 ? (
                  <p className="text-center w-100 my-4">No products found for the selected filters.</p>
                ) : (
                  <>
                    {filteredData.map((Item) => (
                      <ProductCard
                        key={Item.id}
                        product={Item}
                        colClass={colClass}
                        loading={false}
                      />
                    ))}
                    {loadingMore &&
                      Array.from({ length: 4 }).map((_, index) => (
                        <ProductCard
                          key={`more-${index}`}
                          loading={true}
                          colClass={colClass}
                        />
                      ))}
                  </>
                )}
              </div>

              {filteredData.length > 0 && !hasMore && !loadingMore && (
                <div className="text-center my-4">
                  <p className="text-muted">No more items to show</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      <Offcanvas
        className="custom-slide-offcanvas"
        show={showSidebar}
        onHide={() => setShowSidebar(false)}
        placement="start"
      >
        <Offcanvas.Header closeButton>
          <Offcanvas.Title>
            <h6 className="sidebar-heading fs-4 fw-bold p-0 text-start">
              Filter By
            </h6>
          </Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body className="px-4">
          <Form>
            <h5 className="filter-title fw-bold mb-4">Filter by Price</h5>
            <ReactSlider
              min={0}
              max={35000}
              value={value}
              onChange={handleChange}
              renderTrack={(props, state) => {
                const { key, style, ...restProps } = props;
                return (
                  <div
                    key={key}
                    {...restProps}
                    className="slider-track"
                    style={{
                      ...style,
                      background: state.index === 1 ? "#032a54" : "#ccc",
                    }}
                  />
                );
              }}
              renderThumb={(props) => {
                const { key, style, ...restProps } = props;
                return (
                  <div
                    key={key}
                    {...restProps}
                    className="slider-thumb"
                    style={{
                      ...style,
                      border: "1px solid #032a54",
                    }}
                  />
                );
              }}
            />

            <div className="price-range-display pt-4">
              <span>₹{value[0]}</span>{" "}
              <span>{value[1] >= 35000 ? "₹35000+" : `₹${value[1]}`}</span>
            </div>

            <h5 className="filter-title fw-bold mb-3 mt-4">
              Filter by Category
            </h5>
            {["Filter", "Geysers", "Pump", "Memorn"].map((category) => (
              <Form.Check
                key={category}
                type="checkbox"
                id={category}
                label={category}
                value={category}
                onChange={handleCategoryChange}
                checked={selectedCategories.includes(category)}
              />
            ))}
          </Form>
        </Offcanvas.Body>
      </Offcanvas>
    </section>
  );
};

export default HomePageProducts;
